import { useEffect, useState } from "react";
import { Plus, Trash, Users } from "@phosphor-icons/react";
import api from "../../lib/api";

export const FILTER_KINDS = [
  { kind: "tag", label: "Ha il tag", tag: true },
  { kind: "tag_not", label: "Non ha il tag", tag: true },
  { kind: "tipo_cliente", label: "Il tipo contatto è", options: ["nuovo", "abituale", "vip"] },
  { kind: "consenso", label: "Consenso marketing", options: ["si", "no"] },
  { kind: "lingua", label: "La preferenza di lingua è", options: ["it", "en", "fr", "de", "es"] },
  { kind: "inattivo_giorni", label: "Giorni dall'ultima visita più di", numeric: true, noOp: true },
  { kind: "prenotazione_il", label: "Ha una prenotazione il giorno", date: true },
  { kind: "prenotazione_dal", label: "Ha una prenotazione dal giorno", date: true },
  { kind: "prenotazione_entro", label: "Ha una prenotazione entro il giorno", date: true },
  { kind: "prenotazione", label: "Ha prenotato", options: ["si", "no"] },
  { kind: "numero_visite", label: "Numero di visite", numeric: true },
  { kind: "campo_anagrafico", label: "Campo anagrafico", fieldOp: true },
  { kind: "compleanno_mese", label: "Compie gli anni nel mese di", month: true },
  { kind: "creato_dal", label: "Contatto creato a partire dal giorno", date: true },
  { kind: "creato_prima", label: "Contatto creato prima del giorno", date: true },
  { kind: "spesa_totale", label: "Fatturato generato (€)", numeric: true },
  { kind: "coupon", label: "Coupon", options: ["attivo", "utilizzato", "scaduto", "non_utilizzato", "attivo_senza_prenotazione", "scaricato_non_usato_senza_prenotazione"], coupon: true },
  { kind: "ultima_visita", label: "Ultima visita (giorni fa)", numeric: true },
  { kind: "sesso", label: "Il sesso è", options: ["M", "F"] },
  { kind: "blacklist", label: "In blacklist", options: ["si", "no"] },
  { kind: "privacy", label: "Consenso privacy", options: ["si", "no"] },
  { kind: "sorgente", label: "Sorgente acquisizione", options: ["reserve", "external", "promo", "fidelity"] },
  { kind: "spesa_media", label: "Spesa media per visita (€)", numeric: true },
  { kind: "media_coperti", label: "Media coperti per visita", numeric: true },
  { kind: "punti_fidelity", label: "Punti fidelity", numeric: true },
  { kind: "hard_bounce", label: "Email rimbalzata (hard bounce)", options: ["si", "no"] },
  { kind: "campagna", label: "Contattato dalla campagna", options: ["si", "no"], campaign: true },
];

// Voci "piatte" stile Plateform: ogni campo anagrafico e' una voce del menu Campo.
// Internamente restano filtri campo_anagrafico (field preimpostato, niente sotto-menu).
const VIRTUAL_KINDS = [
  { vk: "anag_nome", label: "Nome", field: "nome" },
  { vk: "anag_cognome", label: "Cognome", field: "cognome" },
  { vk: "anag_telefono", label: "Telefono", field: "telefono" },
  { vk: "anag_email", label: "Email", field: "email" },
  { vk: "anag_citta", label: "Città", field: "citta" },
  { vk: "anag_provincia", label: "Provincia", field: "provincia" },
  { vk: "anag_cap", label: "CAP", field: "cap" },
  { vk: "anag_nazione", label: "Nazione", field: "nazione" },
  { vk: "anag_data_nascita", label: "Data di nascita", field: "data_nascita" },
];

const DROPDOWN = [
  ...FILTER_KINDS.filter((k) => k.kind !== "campo_anagrafico")
    .map((k) => ({ vk: k.kind, label: k.label })),
  ...VIRTUAL_KINDS,
].sort((a, b) => a.label.localeCompare(b.label, "it"));

const OPERATORS = [">=", "<=", "=", ">", "<"];
const MONTHS = [["01", "Gennaio"], ["02", "Febbraio"], ["03", "Marzo"], ["04", "Aprile"],
                ["05", "Maggio"], ["06", "Giugno"], ["07", "Luglio"], ["08", "Agosto"],
                ["09", "Settembre"], ["10", "Ottobre"], ["11", "Novembre"], ["12", "Dicembre"]];

export function FiltersEditor({ combinator, filters, onChange }) {
  const [tags, setTags] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  useEffect(() => {
    api.get("/tags").then((r) => setTags(r.data)).catch(() => {});
    api.get("/coupons").then((r) => setCoupons(r.data)).catch(() => {});
    api.get("/marketing/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  }, []);
  const setFilter = (i, patch) => onChange({ combinator, filters: filters.map((f, x) => x === i ? { ...f, ...patch } : f) });
  const addFilter = () => onChange({ combinator, filters: [...filters, { kind: "tag", operator: ">=", value: "" }] });
  const removeFilter = (i) => onChange({ combinator, filters: filters.filter((_, x) => x !== i) });

  const inputCls = "h-10 px-2.5 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900";

  return (
    <div className="space-y-2" data-testid="filters-editor">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-zinc-500">Combina filtri:</span>
        {[["and", "TUTTI (AND)"], ["or", "ALMENO UNO (OR)"]].map(([k, l]) => (
          <button type="button" key={k} onClick={() => onChange({ combinator: k, filters })}
            data-testid={`combinator-${k}`}
            className={`h-8 px-3 rounded-full text-[11px] font-bold border transition ${combinator === k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300"}`}>
            {l}
          </button>
        ))}
      </div>
      {filters.map((f, i) => {
        const def = FILTER_KINDS.find((k) => k.kind === f.kind) || {};
        const currentVk = f.kind === "campo_anagrafico" ? `anag_${f.field || "email"}` : f.kind;
        return (
          <div key={i} className="flex items-center gap-1.5 flex-wrap bg-zinc-50 border border-zinc-200 rounded-md p-2" data-testid={`filter-row-${i}`}>
            <select value={currentVk} onChange={(e) => {
                const vk = e.target.value;
                const virt = VIRTUAL_KINDS.find((x) => x.vk === vk);
                if (virt) {
                  setFilter(i, { kind: "campo_anagrafico", field: virt.field, operator: "=", value: "" });
                  return;
                }
                const nd = FILTER_KINDS.find((k) => k.kind === vk) || {};
                setFilter(i, {
                  kind: vk,
                  operator: nd.fieldOp ? "compilato" : ">=",
                  value: nd.options ? nd.options[0] : nd.numeric ? 1
                    : nd.date ? new Date().toISOString().slice(0, 10)
                    : nd.month ? new Date().toISOString().slice(5, 7) : "",
                  ...(nd.fieldOp ? { field: "email" } : {}),
                });
              }}
              className={inputCls} data-testid={`filter-kind-${i}`}>
              {DROPDOWN.map((d) => <option key={d.vk} value={d.vk}>{d.label}</option>)}
            </select>
            {def.numeric && !def.noOp && (
              <select value={f.operator || ">="} onChange={(e) => setFilter(i, { operator: e.target.value })}
                className={`${inputCls} w-20`} data-testid={`filter-op-${i}`}>
                {OPERATORS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
            {def.numeric && (
              <input type="number" value={f.value ?? 1} onChange={(e) => setFilter(i, { value: parseFloat(e.target.value) || 0 })}
                className={`${inputCls} w-24`} data-testid={`filter-value-${i}`} />
            )}
            {def.options && (
              <select value={f.value ?? def.options[0]} onChange={(e) => setFilter(i, { value: e.target.value })}
                className={inputCls} data-testid={`filter-opt-${i}`}>
                {def.options.map((o) => <option key={o} value={o}>{o.replaceAll("_", " ")}</option>)}
              </select>
            )}
            {def.coupon && (
              <select value={f.coupon_id || ""} onChange={(e) => setFilter(i, { coupon_id: e.target.value })}
                className={inputCls} data-testid={`filter-coupon-${i}`}>
                <option value="">Qualsiasi promozione</option>
                {coupons.map((cp) => <option key={cp.id} value={cp.id}>{cp.name}</option>)}
              </select>
            )}
            {def.campaign && (
              <select value={f.campaign_id || ""} onChange={(e) => setFilter(i, { campaign_id: e.target.value })}
                className={inputCls} data-testid={`filter-campaign-${i}`}>
                <option value="">Seleziona campagna</option>
                {campaigns.map((cp) => <option key={cp.id} value={cp.id}>{cp.name}</option>)}
              </select>
            )}
            {def.text && (
              <input value={f.value || ""} onChange={(e) => setFilter(i, { value: e.target.value })} placeholder="nome tag"
                className={inputCls} data-testid={`filter-text-${i}`} />
            )}
            {def.tag && (
              <select value={f.value ?? ""} onChange={(e) => setFilter(i, { value: e.target.value })}
                className={inputCls} data-testid={`filter-tag-${i}`}>
                <option value="">Seleziona tag</option>
                {tags.map((t) => <option key={t.id || t.name} value={t.name}>{t.name}</option>)}
              </select>
            )}
            {def.date && (
              <input type="date" value={f.value || ""} onChange={(e) => setFilter(i, { value: e.target.value })}
                className={inputCls} data-testid={`filter-date-${i}`} />
            )}
            {def.month && (
              <select value={f.value ?? ""} onChange={(e) => setFilter(i, { value: e.target.value })}
                className={inputCls} data-testid={`filter-month-${i}`}>
                {MONTHS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            )}
            {def.fieldOp && (
              <>
                <select value={f.operator || "compilato"} onChange={(e) => setFilter(i, { operator: e.target.value })}
                  className={inputCls} data-testid={`filter-fieldop-${i}`}>
                  <option value="=">uguale a</option>
                  <option value="compilato">compilato</option>
                  <option value="non_compilato">non compilato</option>
                </select>
                {(f.operator || "compilato") === "=" && (
                  <input value={f.value || ""} onChange={(e) => setFilter(i, { value: e.target.value })} placeholder="valore"
                    className={inputCls} data-testid={`filter-fieldval-${i}`} />
                )}
              </>
            )}
            <button type="button" onClick={() => removeFilter(i)} className="p-2 text-zinc-400 hover:text-rose-600" data-testid={`filter-del-${i}`}>
              <Trash size={15} />
            </button>
          </div>
        );
      })}
      <button type="button" onClick={addFilter} data-testid="filter-add"
        className="h-9 px-3 rounded-md bg-zinc-100 text-xs font-semibold text-zinc-700 flex items-center gap-1.5 hover:bg-zinc-200">
        <Plus size={13} weight="bold" /> Aggiungi condizione
      </button>
    </div>
  );
}

export function RecipientCount({ preview }) {
  if (!preview) return null;
  if (preview.error) {
    return (
      <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-4 text-sm" data-testid="recipient-count-error">
        Errore nel calcolo dei destinatari. Controlla i filtri selezionati.
      </div>
    );
  }
  return (
    <div className="bg-zinc-900 text-white rounded-lg p-4 flex items-center gap-4 flex-wrap" data-testid="recipient-count">
      <Users size={22} weight="bold" />
      <div>
        <div className="font-display text-2xl font-black">{preview.eligible} <span className="text-sm font-semibold opacity-70">destinatari</span></div>
        <div className="text-[11px] opacity-70">
          {preview.matching} nel segmento · {preview.excluded_no_consent} esclusi senza consenso · costo stimato €{preview.estimated_cost}
        </div>
      </div>
    </div>
  );
}
